import { useState } from 'react'
import WireframeLayout, { buildSidebarSections } from '@/components/WireframeLayout'
import { cn, Badge, Card, Button, Spark, AIReasoning, KPI } from './components/primitives'
import { SKU_DATA, ALERTS, AI_LOG } from './components/data'

function AIView() {
  const [er, setEr] = useState<Record<string, boolean>>({});
  const tog = (k: string) => setEr(p => ({ ...p, [k]: !p[k] }));
  const alertBorder: Record<string, string> = { destructive: "#ef4444", warning: "#f59e0b", success: "#10b981", default: "#3b82f6" };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex gap-3 flex-wrap">
        <KPI label="Models Active" value="6" sub="Demand · Inventory · Pricing · Promo · Risk · Allocation" />
        <KPI label="Last Learning Cycle" value="08:00" delta="MAPE improved 2.1%" dir="up" />
        <KPI label="Actions Today" value="14" sub="6 auto-executed · 8 pending review" />
        <KPI label="Forecast Accuracy" value="94.2%" delta="+1.8pp vs last month" dir="up" />
      </div>

      <div>
        <h3 className="text-sm font-semibold text-slate-900 mb-3">Active Alerts & Recommendations</h3>
        <div className="flex flex-col gap-2">
          {ALERTS.map(a => (
            <Card key={a.id} style={{ borderLeft: `3px solid ${alertBorder[a.type] || alertBorder.default}` }}>
              <div className="p-4 flex justify-between items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <Badge variant={a.type}>{a.type === "destructive" ? "critical" : a.type}</Badge>
                    <span className="text-sm font-semibold text-slate-900">{a.title}</span>
                  </div>
                  <p className="text-xs text-slate-500 leading-relaxed">{a.desc}</p>
                  <AIReasoning text={a.reasoning} open={er[a.id]} toggle={() => tog(String(a.id))} />
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className="text-xs text-slate-400">{a.time}</span>
                  <Button variant="accent" size="sm">{a.action}</Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-slate-900 mb-3">Today's AI Actions</h3>
        <Card className="divide-y divide-slate-100">
          {AI_LOG.map((a, i) => (
            <div key={i} className="p-4 flex gap-4 items-start">
              <span className="text-xs text-slate-400 font-mono w-11 flex-shrink-0 pt-0.5">{a.t}</span>
              <div className="w-2 h-2 rounded-full bg-emerald-500 mt-1.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-900">{a.a}</div>
                <div className="text-xs text-slate-500 mt-0.5">{a.d}</div>
                <AIReasoning text={a.r} open={er[`l${i}`]} toggle={() => tog(`l${i}`)} />
              </div>
            </div>
          ))}
        </Card>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-slate-900 mb-3">SKU Health Matrix</h3>
        <Card className="overflow-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-2 border-slate-200">
                {["SKU", "Product", "Stock", "Vel./day", "Days", "Status", "30d Fcst", "Margin", "Channel", "Trend"].map(h => (
                  <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider bg-slate-50/80">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {SKU_DATA.map(s => (
                <tr key={s.id} className="hover:bg-slate-50/50 transition-colors">
                  <td className="px-4 py-2.5 font-mono text-xs text-blue-600 font-medium">{s.id}</td>
                  <td className="px-4 py-2.5 font-medium text-slate-900">{s.name}</td>
                  <td className="px-4 py-2.5 text-slate-700">{s.stock.toLocaleString()}</td>
                  <td className="px-4 py-2.5 text-slate-700">{s.velocity}</td>
                  <td className="px-4 py-2.5"><span className={cn("font-bold", s.dos < 15 ? "text-red-600" : s.dos > 100 ? "text-violet-600" : "text-slate-900")}>{s.dos}</span></td>
                  <td className="px-4 py-2.5"><Badge variant={s.status}>{s.status}</Badge></td>
                  <td className="px-4 py-2.5 text-slate-700">{s.fc.toLocaleString()}</td>
                  <td className="px-4 py-2.5 text-emerald-600 font-semibold">{(s.margin * 100).toFixed(0)}%</td>
                  <td className="px-4 py-2.5 text-slate-500">{s.ch}</td>
                  <td className="px-4 py-2.5"><Spark data={[45, 52, 48, 63, 58, 72, s.velocity]} color={s.status === "critical" ? "#ef4444" : s.status === "overstock" ? "#8b5cf6" : "#10b981"} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
    </div>
  );
}

export default function AtlasAi({ role }: { role: string }) {
  return (
    <WireframeLayout
      title="AI Command Center"
      subtitle="Autonomous planning, forecasting & optimization"
      titleIcon="◈"
      titleColor="#10b981"
      sidebarLinks={buildSidebarSections(role, "atlas-ai")}
    >
      <AIView />
    </WireframeLayout>
  )
}
